/**
 * Concrete SyncEngine wiring for the desktop app.
 *
 * Storage:  localStorage keys (cl_cloud_*)
 * LocalDb:  HTTP to the embedded NestJS local sync endpoints (/v1/sync/local/*)
 */

import { SyncEngine, SyncEngineConfig, SyncStorage, SyncLocalDb } from '@coreledger/sync-engine';
import { api } from '../api/client';

// ── localStorage keys ─────────────────────────────────────────────────────────

const KEY_CLOUD_URL = 'cl_cloud_url';
const KEY_CLOUD_TOKEN = 'cl_cloud_token';
const KEY_DEVICE_ID = 'cl_sync_device_id';
const KEY_CURSOR = 'cl_sync_cursor';

// ── Cloud auth helpers ────────────────────────────────────────────────────────

export function getCloudApiUrl(): string | null {
  return localStorage.getItem(KEY_CLOUD_URL);
}

export function setCloudApiUrl(url: string): void {
  localStorage.setItem(KEY_CLOUD_URL, url);
}

export function getCloudToken(): string | null {
  return localStorage.getItem(KEY_CLOUD_TOKEN);
}

export function setCloudToken(token: string): void {
  localStorage.setItem(KEY_CLOUD_TOKEN, token);
}

export function clearCloudAuth(): void {
  localStorage.removeItem(KEY_CLOUD_URL);
  localStorage.removeItem(KEY_CLOUD_TOKEN);
  localStorage.removeItem(KEY_DEVICE_ID);
  localStorage.removeItem(KEY_CURSOR);
}

// ── SyncStorage — persists deviceId + pull cursor ─────────────────────────────

const desktopStorage: SyncStorage = {
  getDeviceId: () => localStorage.getItem(KEY_DEVICE_ID),
  setDeviceId: (id) => localStorage.setItem(KEY_DEVICE_ID, id),
  getCursor: () => localStorage.getItem(KEY_CURSOR),
  setCursor: (c) => localStorage.setItem(KEY_CURSOR, c),
};

// ── SyncLocalDb — talks to embedded NestJS local endpoints ───────────────────

export type ApplyChangeCallback = (entityId: string, entity: string) => void;
let _applyChangeCallback: ApplyChangeCallback | null = null;

/** Set a one-time callback to track which entityIds are applied during the next pull. */
export function setApplyChangeCallback(cb: ApplyChangeCallback | null): void {
  _applyChangeCallback = cb;
}

const desktopLocalDb: SyncLocalDb = {
  getPendingOps: async () => {
    const res = await api.get<unknown[]>('/sync/local/pending-ops');
    return res as import('@coreledger/sync-engine').PushOp[];
  },

  markOpsAsSynced: async (ids) => {
    await api.post('/sync/local/mark-synced', { ids });
  },

  applyChange: async (change) => {
    await api.post('/sync/local/apply', {
      entity: change.entity,
      entityId: change.entityId,
      operation: change.operation,
      payload: change.payload,
    });
    _applyChangeCallback?.(change.entityId, change.entity);
  },
};

// ── Factory ───────────────────────────────────────────────────────────────────

export function createEngine(cloudUrl: string, deviceName: string): SyncEngine {
  const config: SyncEngineConfig = {
    apiUrl: cloudUrl,
    deviceName,
    batchSize: 100,
    getAuthToken: () => getCloudToken(),
    storage: desktopStorage,
    localDb: desktopLocalDb,
    onLog: (level, msg) => console[level]('[SyncEngine]', msg),
  };
  return new SyncEngine(config);
}
